/**
 * Ernaehrung als Quelle fuer Key Results (F-35 E) - rein und getestet. Die
 * Tage kommen fertig bewertet herein (Score und erfuellte Kriterien), der
 * Wert gilt im Zeitraum des Ziels bzw. der laufenden Woche bis heute.
 */
import type { ErnaehrungsMass, LocalDate } from '@/domain/types';
import { KRITERIEN, type KriteriumArt } from '@/domain/ernaehrung/analyse';
import { startOfWeek } from '@/lib/datetime';

export interface KennTag {
  datum: LocalDate;
  /** Mindestens eine Mahlzeit mit Eintrag - leere Tage des Zeitraums zaehlen nicht (F-38). */
  mitEintrag: boolean;
  vollstaendig: boolean;
  score: number | null;
  /** Erfuellte Kriterien - mit den Grenzen aus den Score-Einstellungen, auch inaktive. */
  erfuellt: ReadonlySet<KriteriumArt>;
}

export const ERNAEHRUNG_MASSE: Record<ErnaehrungsMass['art'], { label: string; einheit: string; anzahl: boolean }> = {
  tage_kriterium: { label: 'Tage mit erfülltem Kriterium', einheit: 'Tage', anzahl: true },
  score_schnitt: { label: 'Ø Ernährungs-Score', einheit: 'Score', anzahl: false },
  tage_vollstaendig: { label: 'Vollständig erfasste Tage', einheit: 'Tage', anzahl: true },
};

export function ernaehrungsWert(m: ErnaehrungsMass, tage: KennTag[], ziel: { von: LocalDate; bis: LocalDate }, heute: LocalDate): number | null {
  const von = m.zeitraum === 'woche' ? startOfWeek(heute) : ziel.von;
  const bis = m.zeitraum === 'woche' || heute < ziel.bis ? heute : ziel.bis;
  const im = tage.filter((t) => t.datum >= von && t.datum <= bis);
  if (m.art === 'tage_kriterium') return im.filter((t) => t.erfuellt.has(m.kriterium)).length;
  if (m.art === 'tage_vollstaendig') return im.filter((t) => t.vollstaendig).length;
  const s = im.flatMap((t) => (t.score !== null ? [t.score] : []));
  return s.length ? Math.round((s.reduce((a, b) => a + b, 0) / s.length) * 10) / 10 : null;
}

/**
 * Kennzahlen eines Zeitraums fuer die Statistik (F-38): Tage mit Eintraegen,
 * vollstaendige Tage und der Durchschnitt der Scores (nur Tage mit Score).
 * Leere Tage zaehlen nirgends mit - ein Zeitraum ohne Eintraege ergibt 0.
 */
export function zeitraumKennzahlen(tage: KennTag[]): { mitEintrag: number; vollstaendig: number; schnitt: number | null } {
  const scores = tage.flatMap((t) => (t.score !== null ? [t.score] : []));
  return {
    mitEintrag: tage.filter((t) => t.mitEintrag).length,
    vollstaendig: tage.filter((t) => t.vollstaendig).length,
    schnitt: scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null,
  };
}

export function ernaehrungsMassText(m: ErnaehrungsMass): string {
  const zr = m.zeitraum === 'woche' ? 'diese Woche' : 'im Zielzeitraum';
  if (m.art === 'tage_kriterium') return `Tage „${KRITERIEN[m.kriterium].label}“, ${zr}`;
  return `${ERNAEHRUNG_MASSE[m.art].label}, ${zr}`;
}
